import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/Checkbox';
import { Close } from '@/components/Icons/Close';
import { SearchOutline } from '@/components/Icons/SearchOutline';
import { MailActionsMenu } from '@/pages/Mail/components/MailActionsMenu';
import { MailFolderDropdown } from '@/pages/Mail/components/MailFolderDropdown';
import { getMailFolderActions } from '@/pages/Mail/mailFolders';
import { MailFolderType } from '@/types/components';
import type { MailFolder } from '@/types/mail';
import { tv } from '@/utils/tv';

const actionButton = tv({
  base: 'text-callout-16 cursor-pointer transition-colors active:text-primary-pressed disabled:cursor-not-allowed',
  variants: {
    hasSelection: {
      true: 'text-primary',
      false: 'text-primary/40',
    },
  },
});

interface Props {
  folders: MailFolder[];
  folder: MailFolder;
  count: number;
  isSelecting: boolean;
  selectedCount: number;
  areAllSelected: boolean;
  onFolderChange: (folderType: MailFolderType) => void;
  onSearch: () => void;
  onMark: () => void;
  onMarkAllRead: () => void;
  onMarkSelectedRead: () => void;
  onToggleAll: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onDeletePermanently: () => void;
  onCancel: () => void;
}

export const MailMobileToolbar: React.FC<Props> = ({
  folders,
  folder,
  count,
  isSelecting,
  selectedCount,
  areAllSelected,
  onFolderChange,
  onSearch,
  onMark,
  onMarkAllRead,
  onMarkSelectedRead,
  onToggleAll,
  onDelete,
  onRestore,
  onDeletePermanently,
  onCancel,
}) => {
  const { t } = useTranslation();

  const {
    canSelect,
    canMarkAllAsRead,
    canMarkAsRead,
    canDelete,
    canRestore,
    canDeletePermanently,
  } = getMailFolderActions(folder);
  const canSearch = folder.searchTypes.length > 0;
  const hasSelection = selectedCount > 0;

  return (
    <div className="flex flex-col gap-3 px-4 pt-2 pb-3">
      <div className="flex items-center gap-2">
        <MailFolderDropdown
          folders={folders}
          folder={folder}
          count={count}
          onChange={onFolderChange}
        />

        <div className="ml-auto flex items-center gap-3">
          {!isSelecting && canSearch && (
            <button
              type="button"
              aria-label={t('mail.search')}
              onClick={onSearch}
              className="flex size-6 cursor-pointer items-center justify-center text-primary"
            >
              <SearchOutline className="size-5.5" strokeWidth="1.6" />
            </button>
          )}

          {isSelecting && canSearch && (
            <span className="flex size-5.5 items-center justify-center text-primary/40">
              <SearchOutline className="size-5.5" strokeWidth="1.6" />
            </span>
          )}

          {!isSelecting && count > 0 && (
            <MailActionsMenu
              triggerClassName="size-6"
              canSelect={canSelect}
              canMarkAllAsRead={canMarkAllAsRead}
              onMark={onMark}
              onMarkAllRead={onMarkAllRead}
            />
          )}

          {isSelecting && (
            <button
              type="button"
              aria-label={t('global.close')}
              onClick={onCancel}
              className="flex size-6 cursor-pointer items-center justify-center text-primary"
            >
              <Close className="size-6" />
            </button>
          )}
        </div>
      </div>

      {isSelecting && (
        <div className="flex items-center gap-3">
          <div
            onClick={onToggleAll}
            className="flex cursor-pointer items-center gap-2"
          >
            <Checkbox checked={areAllSelected} onChange={() => {}} />
            {hasSelection && (
              <span className="text-body-14 text-muted-foreground">
                {selectedCount}
              </span>
            )}
          </div>

          <div className="ml-auto flex items-center gap-7">
            {canMarkAsRead && (
              <button
                type="button"
                disabled={!hasSelection}
                onClick={onMarkSelectedRead}
                className={actionButton({ hasSelection })}
              >
                {t('mail.markAsRead')}
              </button>
            )}
            {canRestore && (
              <button
                type="button"
                disabled={!hasSelection}
                onClick={onRestore}
                className={actionButton({ hasSelection })}
              >
                {t('mail.restore')}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                disabled={!hasSelection}
                onClick={onDelete}
                className={actionButton({ hasSelection })}
              >
                {t('mail.delete')}
              </button>
            )}
            {canDeletePermanently && (
              <button
                type="button"
                disabled={!hasSelection}
                onClick={onDeletePermanently}
                className={actionButton({ hasSelection })}
              >
                {t('mail.deletePermanently')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
